<template>
  <v-skeleton-loader
    :loading="skeleton"
    type="sentences"
  >
    <v-autocomplete
      ref="autocomplete"
      v-model="model"
      v-bind="$attrs"
      :items="list"
      :loading="isLoading"
      :search-input="search"
      :menu-props="menuProps"
      :item-disabled="itemDisabled"
      :item-text="itemText"
      :item-value="itemValue"
      no-filter
      outlined
      dense
      @focus="focus"
      @input="onInput"
      @update:search-input="onSearchInput"
    >
      <template #no-data>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>
              {{ noDataText }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <template #item="{ on, item, attrs }">
        <v-list-item :class="attrs.disabled ? 'v-list-item--disabled' : ''" v-on="on">
          <v-list-item-content>
            <v-list-item-title
              class="body-2 font-weight-regular"
              style="white-space: initial;"
              v-text="getText(item)"
            />
            <v-list-item-subtitle
              v-if="itemSubText"
              class="font-weight-regular grey--text text--lighten-1"
              style="white-space: initial;"
              v-text="getSubText(item)"
            />
          </v-list-item-content>


          <v-list-item-action v-if="attrs.disabled" style="pointer-events: auto !important;">
            <BTableButtonAction
              :action="deleteAction"
              :data="item"
              :index="0"
              :list="items"
              v-on="$listeners"
            />
          </v-list-item-action>
        </v-list-item>
      </template>

      <template v-if="canGetDataTotal && !isLoading && !items.length" #append-item>
        <div
          v-observe-visibility="observeVisibility"
          :class="{ 'mx-4 my-2': res = list.length > 1 && canGetData }"
        >
          <v-btn
            v-if="res"
            :loading="isLoading"
            depressed
            small
            block
            @click="onGetNextPage"
            v-text="'Загрузить еще'"
          />
        </div>
      </template>
    </v-autocomplete>
  </v-skeleton-loader>
</template>

<script>
import { CancelToken } from 'axios'
import { ObserveVisibility } from 'vue-observe-visibility'
import uniqueId from 'lodash/uniqueId'
import { DELETE } from '@/helpers/enums/table-actions'
import { Subject, timer } from 'rxjs'
import {
  debounce, distinctUntilChanged, filter, map, switchMap, tap,
} from 'rxjs/operators'

export const BTableButtonAction = () => import(
  /* webpackChunkName: "BTableButtonAction" */
  '@/components/common/BTable/BTableButtonAction.vue'
)

const DEFAULT_PER_PAGE = 50
const DEBAUNCE_TIME = 300

/**
 * Компонент используется для поиска данных и обновлении модели посредством API.
 * Сам компонент не хранит в себе данных, после выбора элемента оповещает родительский компонент и очищает состояние.
 */
export default {
  name: 'BAsyncAutocomplete',
  directives: {
    ObserveVisibility,
  },
  components: {
    BTableButtonAction,
  },
  props: {
    /**
     * Список элементов, которые уже были добавлены (для возможности удаления этих элементов из компонента).
     */
    value: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    request: {
      type: Function,
      required: true,
    },
    itemText: {
      type: [String, Function],
      default: 'name',
    },
    itemSubText: {
      type: [String, Function],
      default: null,
    },
    itemValue: {
      type: [String, Function],
      default: 'id',
    },
    skeleton: Boolean,
  },
  data: (vm) => ({
    model: null,
    isLoading: false,
    list: vm.items,
    meta: null,
    search: null,

    menuRef: null,
    menuProps: {
      maxWidth: '480px',
      contentClass: uniqueId('b-autocomplete__content-class-'),
    },

    deleteAction: DELETE,
  }),
  computed: {
    noDataText() {
      return this.isLoading ? 'Пожалуйста, подождите...' : (this.$attrs?.['no-data-text'] || 'Нет данных')
    },
    isMenuOpened() {
      return this.menuRef && this.menuRef.isActive
    },
    canGetData() {
      return this.list.length !== this.meta?.count
    },
    getValue() {
      const { itemValue } = this
      return typeof itemValue === 'function' ? itemValue : (o) => (o?.[itemValue] || o)
    },
    getText() {
      const { itemText, itemSubText } = this
      return typeof itemText === 'function' ? itemText : (o) => ((o?.[itemText] ?? o?.[itemSubText]) || o)
    },
    getSubText() {
      const { itemSubText } = this
      return typeof itemSubText === 'function' ? itemSubText : (o) => (o?.[itemSubText] || o)
    },
    itemDisabled() {
      const { value, getValue } = this
      if (!value?.length) return void 0

      const excludeIds = value.map(getValue)
      return (data) => excludeIds.includes(getValue(data))
    },
    canGetDataTotal() {
      const { meta } = this
      const result = this.list.length !== meta?.count
      return result || !!meta?.search
    },
    observeVisibility() {
      const { isMenuOpened, canGetDataTotal } = this
      return isMenuOpened && canGetDataTotal ? {
        callback: this.onVisibilityChange,
        throttle: 300,
        throttleOptions: {
          leading: 'visible',
        },
        intersection: {
          root: this.getRefList(),
          rootMargin: '40px',
          threshold: 1.0,
        },
      } : false
    },
  },
  mounted() {
    this.setSubscription()
  },
  methods: {
    onInput() {
      this.$emit('action:append', this.model)
      this.$nextTick(() => {
        this.model = null
        if (this.menuRef && !this.menuRef.isActive) {
          this.menuRef.isActive = true
        }
      })
    },
    setSubscription() {
      const subject$ = new Subject()
        .pipe(
          debounce((_) => timer(_?.force ? 1 : DEBAUNCE_TIME)),
          map((_) => ({ ..._, search: _.search?.trim?.() || void 0 })),
          distinctUntilChanged((a, b) => (a?.search === b?.search) && !b.force),
          tap(() => { this.isLoading = true; this.list = []; this.meta = null }),
          switchMap(this.onGetData),
          tap(() => { this.isLoading = false }),
          filter(Boolean),
          tap(({ data, meta }) => { this.list = data; this.meta = meta }),
        )

      this.subject$ = subject$

      const subscription = subject$.subscribe()

      this.$once('hook:beforeDestroy', () => {
        subscription.unsubscribe()
      })
    },
    // eslint-disable-next-line camelcase
    onSearchInput(rawSearch, per_page) {
      const search = rawSearch?.toString()

      this.search = search

      this.subject$.next({ search, per_page })
    },
    async focus() {
      if (!this.list.length) {
        this.isLoading = true
        const { data, meta } = await this.onGetData()
        if (data) {
          this.meta = meta
          this.list = data
        }
        this.isLoading = false
      }

      if (!this.menuRef && this.$refs.autocomplete) {
        this.menuRef = this.$refs.autocomplete.$refs.menu
      }
    },
    // eslint-disable-next-line camelcase
    async onGetData({ per_page, ...args } = { per_page: DEFAULT_PER_PAGE }) {
      // eslint-disable-next-line no-unused-expressions
      this.cancelSource?.cancel(this.name)

      this.cancelSource = CancelToken.source()
      const cancelToken = this.cancelSource.token
      const payload = { ...args, per_page }
      // payload.search = payload.search || this.meta?.search

      try {
        const { meta, data } = await this.request(payload, { cancelToken })
        return { data, meta: { ...payload, ...meta } }
      } catch (e) {
        // ..
      }
      return void 0
    },
    getRefList() {
      const selector = `.${this.menuProps.contentClass}`
      return document.querySelector(selector)
    },
    onVisibilityChange(isVisible) {
      if (!isVisible) return
      if (!this.canGetData) return
      if (this.isLoading) return
      if (this.meta) this.onGetNextPage()
    },
    async onGetNextPage() {
      this.isLoading = true

      const params = {
        ...this.meta,
        page: this.meta.page + 1,
      }

      const response = await this.onGetData(params)

      this.isLoading = false

      if (!response) return

      const { meta, data } = response
      this.meta = meta
      this.list = this.list.concat(data)

      const refList = this.getRefList()
      // TODO: хак что бы список подгрузился
      if (refList) refList.scrollTop -= 1
    },
  },
}
</script>
